import { useState } from "react";
import dayjs from "dayjs";

import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Table from "react-bootstrap/Table";
import { FaEllipsisV, FaEye, FaInfoCircle } from "react-icons/fa";

import { formatIdentifier } from "@/utils/formatIdentifier";
import { handleColumnToggle } from "@/utils/handleColumnTogle";
import { getAge } from "@/utils/dateUtil";
import { getCitizenScoreStatusDescriptionIndicatorC7 } from "@/utils/indicatorsEsf";
import { DetailedCitizenBreastCancerProceduresIndicatorC7Data } from "@/services/esus/indicator/esf/c7/getDetailedCitizensBreastCancerProceduresIndicatorC7";
import { Column } from "@/@types/generics/Column";

import { TableSkeletons } from "@/components/TableSkeletons";
import { CardOverlayTrigger } from "@/components/CardOverlayTrigger";

type IndicatorEsfC7ProcedureTableColumns = {
	citizenName: Column;
	citizenAge: Column;
	document: Column;
	healthTeam: Column;
	microarea: Column;
	lastProcedure: Column;
};

type IndicatorEsfC7CitizensProceduresTableProps = {
	citizensWithProcedures: DetailedCitizenBreastCancerProceduresIndicatorC7Data[];
	isLoading: boolean;
	openCitizenHistory: (
		citizenWithProcedures: DetailedCitizenBreastCancerProceduresIndicatorC7Data
	) => void;
};

export function IndicatorEsfC7CitizensProceduresTable({
	citizensWithProcedures,
	isLoading,
	openCitizenHistory,
}: Readonly<IndicatorEsfC7CitizensProceduresTableProps>) {
	const [columns, setColumns] = useState<IndicatorEsfC7ProcedureTableColumns>({
		citizenName: { title: "Nome do cidadão", visible: true },
		citizenAge: { title: "Idade", visible: true },
		document: { title: "Documento", visible: true },
		healthTeam: { title: "Equipe", visible: false },
		microarea: { title: "MA", visible: false },
		lastProcedure: { title: "Último exame", visible: true },
	});

	return (
		<Table striped bordered hover responsive>
			<thead className="text-center fs-7">
				<tr className="fw-bolder text-center">
					<th className={`text-start px-2 ${!columns.citizenName.visible && "d-none"}`}>
						{columns.citizenName.title}
					</th>
					<th className={`px-2 ${!columns.citizenAge.visible && "d-none"}`}>
						{columns.citizenAge.title}
					</th>
					<th className={`px-2 ${!columns.document.visible && "d-none"}`}>
						{columns.document.title}
					</th>
					<th className={`px-2 ${!columns.healthTeam.visible && "d-none"}`}>
						{columns.healthTeam.title}
					</th>
					<th className={`px-2 ${!columns.microarea.visible && "d-none"}`}>
						{columns.microarea.title}
					</th>
					<th className={`px-2 ${!columns.lastProcedure.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.lastProcedure.title}
							<CardOverlayTrigger
								header="Último exame"
								message="Último exame de rastreamento para câncer de mama, solicitado ou avaliado nos últimos 24 meses"
								icon={<FaInfoCircle className="text-muted" />}
							/>
						</div>
					</th>
					<th className="text-nowrap pb-0">
						Ações
						<OverlayTrigger
							trigger="click"
							placement="bottom"
							overlay={
								<Popover>
									<Popover.Body>
										<Form className="d-flex flex-column gap-2">
											{Object.keys(columns).map((key, index) => (
												<Form.Check
													key={`citizen-attendances-table-column-${
														index + 1
													}`}
													type="checkbox"
													label={
														columns[
															key as keyof IndicatorEsfC7ProcedureTableColumns
														].title
													}
													checked={
														columns[
															key as keyof IndicatorEsfC7ProcedureTableColumns
														].visible
													}
													onChange={() =>
														handleColumnToggle(
															key as keyof IndicatorEsfC7ProcedureTableColumns,
															setColumns
														)
													}
												/>
											))}
										</Form>
									</Popover.Body>
								</Popover>
							}
							rootClose
						>
							<Button
								title="Exibição de colunas da tabela"
								variant="white"
								className="px-2"
								disabled={isLoading}
							>
								<FaEllipsisV />
							</Button>
						</OverlayTrigger>
					</th>
				</tr>
			</thead>
			<tbody className="fs-8">
				{!isLoading && citizensWithProcedures.length > 0
					? citizensWithProcedures.map((citizenWithProcedures, index) => {
							return (
								<tr key={`citizen-attendances-table-row-${index + 1}`}>
									<td
										className={`px-2 ${
											!columns.citizenName.visible && "d-none"
										}`}
									>
										<div className="d-flex flex-column">
											<div>{citizenWithProcedures.fullName}</div>
											<div className="text-muted">
												{citizenWithProcedures.birthdate
													? dayjs(citizenWithProcedures.birthdate).format(
															"DD/MM/YYYY"
													  )
													: ""}
											</div>
										</div>
									</td>
									<td
										className={`text-center text-nowrap px-2 ${
											!columns.citizenAge.visible && "d-none"
										}`}
									>
										{citizenWithProcedures.birthdate
											? getAge(citizenWithProcedures.birthdate)
											: ""}
									</td>
									<td
										className={`text-center text-nowrap px-2 ${
											!columns.document.visible && "d-none"
										}`}
									>
										{citizenWithProcedures.cpf
											? formatIdentifier(citizenWithProcedures.cpf)
											: citizenWithProcedures.cns ?? ""}
									</td>
									<td
										className={`px-2 ${
											!columns.healthTeam.visible && "d-none"
										}`}
									>
										<div className="d-flex flex-column">
											<div>
												{citizenWithProcedures.healthTeam?.name ?? ""}
											</div>
											<div className="text-muted">
												{citizenWithProcedures.healthTeam?.ine ?? ""}
											</div>
										</div>
									</td>
									<td
										className={`text-center text-nowrap px-2 ${
											!columns.microarea.visible && "d-none"
										}`}
									>
										{citizenWithProcedures.area ?? "FA"}
									</td>
									<td
										className={`text-center px-2 ${
											!columns.lastProcedure.visible && "d-none"
										}`}
									>
										<div className="d-flex flex-column">
											<div>
												{getCitizenScoreStatusDescriptionIndicatorC7(
													citizenWithProcedures.lastBreastCancerProcedureStatus
												)}
											</div>
											<div className="text-muted">
												{citizenWithProcedures.attendanceProcedureReqEvals?.at(
													0
												)?.date
													? dayjs(
															citizenWithProcedures.attendanceProcedureReqEvals.at(
																0
															)?.date
													  ).format("DD/MM/YYYY")
													: ""}
											</div>
										</div>
									</td>
									<td className="text-center">
										<Button
											className="bg-primary py-1 px-3"
											onClick={() =>
												openCitizenHistory(citizenWithProcedures)
											}
											title="Histórico do cidadão"
										>
											<FaEye className="fs-4" />
										</Button>
									</td>
								</tr>
							);
					  })
					: isLoading && (
							<TableSkeletons
								numberOfCells={
									Object.values(columns).reduce(
										(count, column) => count + (column?.visible ? 1 : 0),
										0
									) + 1
								}
							/>
					  )}
			</tbody>
		</Table>
	);
}
